// src/components/features/content/ContentSchedule.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Breadcrumbs,
  Link,
  CardContent,
  CardActions,
  CardHeader,
  Switch,
  FormControlLabel,
  useTheme,
  alpha,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Send as PublishIcon,
  NavigateNext as NextIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format, addDays, setHours, setMinutes, isAfter } from 'date-fns';

// Import custom components
import { Card, MagicLoader } from '../../../design-system';

// Import hooks
import useContentStore from '../../../stores/contentStore';
import useNotificationStore from '../../../stores/notificationStore';
import ContentTypeIcon from '../../ui/ContentTypeIcon';
import { EntityStatusBadge } from '../../core';

// Platform Options with icons and colors
const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook', color: '#1877F2' },
  { value: 'instagram', label: 'Instagram', color: '#E1306C' },
  { value: 'twitter', label: 'Twitter', color: '#1DA1F2' },
  { value: 'linkedin', label: 'LinkedIn', color: '#0077B5' },
  { value: 'youtube', label: 'YouTube', color: '#FF0000' },
  { value: 'tiktok', label: 'TikTok', color: '#000000' },
  { value: 'snapchat', label: 'Snapchat', color: '#FFFC00' },
];

// Platform Chip Component
const PlatformChip = ({ platform, selected = false, onClick }) => {
  const platformInfo = PLATFORM_OPTIONS.find(p => p.value === platform) || 
    { value: platform, label: platform?.charAt(0)?.toUpperCase() + platform?.slice(1), color: '#9e9e9e' };
  
  return (
    <Chip 
      label={platformInfo.label}
      onClick={onClick}
      sx={{ 
        bgcolor: selected ? alpha(platformInfo.color, 0.1) : 'transparent',
        color: platformInfo.color,
        borderColor: platformInfo.color,
        fontWeight: selected ? 'medium' : 'normal',
        '&:hover': {
          bgcolor: alpha(platformInfo.color, 0.2),
        }
      }}
      variant={selected ? 'filled' : 'outlined'}
    />
  );
};

// Platform Distribution Card
const PlatformDistributionCard = ({ distribution, onUpdate, onRemove }) => {
  const theme = useTheme();
  const [localDistribution, setLocalDistribution] = useState(distribution);
  const [scheduledDate, setScheduledDate] = useState(
    distribution.scheduledTime ? new Date(distribution.scheduledTime) : new Date()
  );
  const [scheduledTime, setScheduledTime] = useState(
    distribution.scheduledTime ? new Date(distribution.scheduledTime) : new Date()
  );
  
  // Update parent on change
  useEffect(() => {
    // Combine date and time
    if (localDistribution.status === 'scheduled') {
      const combinedDateTime = new Date(scheduledDate);
      combinedDateTime.setHours(scheduledTime.getHours());
      combinedDateTime.setMinutes(scheduledTime.getMinutes());
      
      const updatedDistribution = {
        ...localDistribution,
        scheduledTime: combinedDateTime.toISOString()
      };
      
      onUpdate(updatedDistribution);
    } else {
      onUpdate(localDistribution);
    }
  }, [localDistribution, scheduledDate, scheduledTime]);
  
  // Handle status change
  const handleStatusChange = (event) => {
    setLocalDistribution({
      ...localDistribution,
      status: event.target.value
    });
  };
  
  // Handle platform specific text change
  const handleTextChange = (event) => {
    setLocalDistribution({
      ...localDistribution,
      platformSpecificContent: {
        ...localDistribution.platformSpecificContent,
        text: event.target.value
      }
    });
  };
  
  // Handle hashtags change
  const handleHashtagsChange = (event) => {
    setLocalDistribution({
      ...localDistribution,
      platformSpecificContent: {
        ...localDistribution.platformSpecificContent,
        hashtags: event.target.value.split(',').map(tag => tag.trim())
      }
    });
  };
  
  // Get platform info
  const platformInfo = PLATFORM_OPTIONS.find(p => p.value === distribution.platform) || 
    { value: distribution.platform, label: distribution.platform?.charAt(0)?.toUpperCase() + distribution.platform?.slice(1), color: '#9e9e9e' };
  
  return (
    <Card 
      level={1}
      sx={{
        borderLeft: `4px solid ${platformInfo.color}`,
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PlatformChip platform={distribution.platform} selected />
            <Typography sx={{ ml: 1 }} variant="h6">
              {platformInfo.label}
            </Typography>
          </Box>
        }
        action={
          <IconButton onClick={() => onRemove(distribution.platform)} color="error">
            <DeleteIcon />
          </IconButton>
        }
      />
      
      <Divider />
      
      <CardContent>
        <Grid container spacing={3}>
          {/* Distribution Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Distribution Status</InputLabel>
              <Select
                value={localDistribution.status}
                label="Distribution Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="draft">Draft (Save for Later)</MenuItem>
                <MenuItem value="scheduled">Schedule for Publishing</MenuItem>
              </Select>
              <FormHelperText>
                Choose whether to schedule this content or save as draft
              </FormHelperText>
            </FormControl>
          </Grid>
          
          {/* Schedule Date & Time - Show only if status is "scheduled" */}
          {localDistribution.status === 'scheduled' && (
            <>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Scheduled Date"
                    value={scheduledDate}
                    onChange={(newDate) => setScheduledDate(newDate)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={new Date()}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Scheduled Time"
                    value={scheduledTime}
                    onChange={(newTime) => setScheduledTime(newTime)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          
          {/* Platform Specific Content */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={`${platformInfo.label} Specific Text (Optional)`}
              value={localDistribution.platformSpecificContent?.text || ''}
              onChange={handleTextChange}
              placeholder={`Leave blank to use the main content text, or customize for ${platformInfo.label}...`}
            />
          </Grid>
          
          {/* Platform Specific Hashtags */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={`${platformInfo.label} Specific Hashtags (Optional)`}
              value={localDistribution.platformSpecificContent?.hashtags?.join(', ') || ''}
              onChange={handleHashtagsChange}
              placeholder="Comma-separated hashtags specific to this platform"
              helperText="Leave blank to use the main hashtags, or customize for this platform"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Content Schedule Component
const ContentSchedule = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentContent,
    isLoading,
    error,
    fetchContentById,
    scheduleContent,
    clearCurrentContent,
    clearError
  } = useContentStore();
  
  const { showSuccess, showError } = useNotificationStore();
  
  // Local state
  const [distributions, setDistributions] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Load content on mount
  useEffect(() => {
    loadContent();
    
    return () => {
      clearCurrentContent();
    };
  }, [id]);
  
  // Update distributions when content is loaded
  useEffect(() => {
    if (currentContent) {
      // Initialize distributions from current content
      const contentDistributions = currentContent.distributions || [];
      
      // Prepare distributions with platform specific content if not already present
      const preparedDistributions = contentDistributions.map(dist => {
        if (!dist.platformSpecificContent) {
          return {
            ...dist,
            platformSpecificContent: {
              text: '',
              hashtags: currentContent.hashtags || [],
            }
          };
        }
        return dist;
      });
      
      setDistributions(preparedDistributions);
      
      // Set available platforms (excluding already added ones)
      updateAvailablePlatforms(preparedDistributions);
    }
  }, [currentContent]);
  
  // Load content
  const loadContent = async () => {
    try {
      await fetchContentById(id);
    } catch (error) {
      showError('Failed to load content: ' + error.message);
    }
  };
  
  // Update available platforms
  const updateAvailablePlatforms = (currentDistributions) => {
    const usedPlatforms = currentDistributions.map(dist => dist.platform);
    const available = PLATFORM_OPTIONS.filter(platform => !usedPlatforms.includes(platform.value));
    setAvailablePlatforms(available);
    
    // Reset selected platform if it's no longer available
    if (selectedPlatform && usedPlatforms.includes(selectedPlatform)) {
      setSelectedPlatform('');
    } else if (available.length > 0 && !selectedPlatform) {
      setSelectedPlatform(available[0].value);
    }
  };
  
  // Handle platform selection
  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
  };
  
  // Add platform distribution
  const handleAddPlatform = () => {
    if (!selectedPlatform) return;
    
    // Create new distribution
    const newDistribution = {
      platform: selectedPlatform,
      status: 'draft',
      scheduledTime: new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
      platformSpecificContent: {
        text: '',
        hashtags: currentContent?.hashtags || [],
      }
    };
    
    const updatedDistributions = [...distributions, newDistribution];
    setDistributions(updatedDistributions);
    updateAvailablePlatforms(updatedDistributions);
  };
  
  // Update distribution
  const handleUpdateDistribution = (updatedDistribution) => {
    const updatedDistributions = distributions.map(dist => 
      dist.platform === updatedDistribution.platform ? updatedDistribution : dist
    );
    setDistributions(updatedDistributions);
  };
  
  // Remove distribution
  const handleRemoveDistribution = (platform) => {
    const updatedDistributions = distributions.filter(dist => dist.platform !== platform);
    setDistributions(updatedDistributions);
    updateAvailablePlatforms(updatedDistributions);
  };
  
  // Save distribution settings
  const handleSave = async () => {
    // Validate scheduled times for scheduled distributions
    const now = new Date();
    const invalidDistributions = distributions.filter(dist => 
      dist.status === 'scheduled' && !isAfter(new Date(dist.scheduledTime), now)
    );
    
    if (invalidDistributions.length > 0) {
      showError('Scheduled time must be in the future for all scheduled platforms');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await scheduleContent(id, distributions);
      showSuccess('Content scheduled successfully');
      navigate(`/content/${id}`);
    } catch (error) {
      showError('Failed to schedule content: ' + error.message);
      setIsSubmitting(false);
    }
  };
  
  // Get content title
  const getContentTitle = () => {
    return currentContent?.title || 'Content';
  };
  
  // Loading state
  if (isLoading && !currentContent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <MagicLoader variant="magical" size="medium" message="Loading content..." />
      </Box>
    );
  }
  
  // Error state
  if (error && !currentContent) {
    return (
      <Card 
        level={1} 
        sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading Content
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/content')}
        >
          Back to Content
        </Button>
      </Card>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link 
          component={RouterLink} 
          to="/content"
          color="inherit"
          underline="hover"
        >
          Content
        </Link>
        <Link 
          component={RouterLink} 
          to={`/content/${id}`}
          color="inherit"
          underline="hover"
        >
          {getContentTitle()}
        </Link>
        <Typography color="text.primary">
          Schedule
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Card 
        level={2}
        magical={true}
        sx={{ mb: 4 }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ContentTypeIcon type={currentContent?.type || 'post'} size="medium" />
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 'medium' }}>
                  Schedule: {getContentTitle()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EntityStatusBadge status={currentContent?.status || 'draft'} entityType="content" />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Choose when and where to publish your content
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/content/${id}`)}
            >
              Back to Content
            </Button>
          </Box>
        </Box>
      </Card>
      
      {/* Distribution Settings */}
      <Grid container spacing={3}>
        {/* Currently Selected Platforms */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Platform Distribution
          </Typography>
          
          {distributions.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
              <InfoIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                No platforms selected for distribution
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Add platforms below to schedule your content
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {distributions.map(distribution => (
                <Grid item xs={12} key={distribution.platform}>
                  <PlatformDistributionCard
                    distribution={distribution}
                    onUpdate={handleUpdateDistribution}
                    onRemove={handleRemoveDistribution}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        
        {/* Add Platforms */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Add Platform
            </Typography>
            
            {availablePlatforms.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                All available platforms have been added
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {availablePlatforms.map(platform => (
                    <PlatformChip
                      key={platform.value}
                      platform={platform.value}
                      selected={selectedPlatform === platform.value}
                      onClick={() => handlePlatformSelect(platform.value)}
                    />
                  ))}
                </Box>
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddPlatform}
                  disabled={!selectedPlatform}
                >
                  Add Platform
                </Button>
              </>
            )}
          </Paper>
        </Grid>
        
        {/* Save Changes */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/content/${id}`)}
            >
              Cancel
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSubmitting}
                loading={isSubmitting}
                magical={true}
              >
                Save Distribution Settings
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContentSchedule;